import React from 'react'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import {
  MetricCard,
  StatsCard,
  Divider,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { CompanyTypeSlug } from 'models/companies'
import { formatInteger } from 'utils/formatting'
import { Link } from 'react-router-dom'

export function NewUnmatchedCompanyPage({ companyID }: { companyID: number }) {
  const api = apiService()
  const { data: companyData, isLoading } = api.useGetCompany(companyID)

  // Set document title
  useDocumentTitle(`${companyData?.name || 'Not Found'} (Company) - First Bite`)

  // Modal state management
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  if (isLoading) {
    return <LoadingScreen />
  }

  // Format phone with extension
  const formatPhone = () => {
    if (!companyData?.phone_number) return undefined
    return companyData.extension
      ? `${companyData.phone_number} ext. ${companyData.extension}`
      : companyData.phone_number
  }

  // Build stats for the summary tab
  const companyStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Category',
      value: companyData?.category || '-',
    },
    {
      label: 'Sub-Category',
      value: (
        <Link
          className="text-fb-green-900 decoration-fb-green-700 underline"
          to={`/opportunities/${companyData?.category_slug}/${companyData?.sub_category?.slug}/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {companyData?.sub_category?.name || '-'}
        </Link>
      ),
    },
    {
      label: 'City',
      value: companyData?.city || '-',
    },
    {
      label: 'State',
      value: companyData?.state || '-',
    },
    {
      label: 'Zip Code',
      value: companyData?.zipcode || '-',
    },
  ]

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      phone={formatPhone()}
      address={companyData?.address}
      googlePlaceUrl={companyData?.google_place_url || undefined}
    />
  )

  // Door Count Metric (for unmatched doors)
  summaryTabContent.push([
    <MetricCard
      key="doors"
      value={formatInteger(companyData?.count || 0)}
      label="Unmatched Doors"
      actionText="View Doors"
      onActionClick={() => setShowDoorsModal(true)}
    />,
    <Divider key="divider-1" />,
    <StatsCard key="stats" stats={companyStats} />,
  ])

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData!.id!}
        breadcrumbRoot={`${companyData?.category || 'Commercial'} - ${
          companyData?.sub_category?.name || 'Company'
        }`}
        breadcrumbs={[
          {
            title: 'Company',
            breadcrumb: companyData!.name!,
            url: undefined,
          },
        ]}
        sidebar={{
          header: {
            name: companyData!.name!,
            domain: companyData!.domain || '',
            isUserCreated: true,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData!.id!}
          companyName={companyData!.name}
          websiteAddress={companyData!.domain!}
          companyTypeSlug={companyData!.category! as CompanyTypeSlug}
          tableTypeSlug={companyData!.category! as CompanyTypeSlug}
          isUserCreated={true}
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {companyData && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={companyData.id!}
          chainData={{
            id: companyData.id!,
            name: companyData.name!,
            domain: companyData.domain,
          }}
          subCategory={companyData.sub_category}
          companyName={companyData.name}
        />
      )}
    </>
  )
}
